import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "./ui/scroll-area";
import { FileText } from "lucide-react";

const TermsAndConditions = () => {
  const { t } = useTranslation("settings");
  return (
    <>
      <Dialog>
        <DialogTrigger asChild>
          <span className="underline cursor-pointer hover:text-[--accent-primary] transition-colors text-[--accent-primary]">
            Terms and Conditions
          </span>
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader className="border-b border-border/50 pb-4 flex flex-row items-center gap-4">
            <div className="flex items-center justify-center p-2 rounded-lg bg-[--accent-primary]/15">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl font-bold">
                Terms &amp; Conditions
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Last updated: July 2026
              </p>
            </div>
          </DialogHeader>

          <ScrollArea className="h-[50vh] pr-4 my-4">
            <div className="space-y-6 text-sm leading-relaxed text-slate-300">
              <div className="bg-[#0c1938]/30 border border-[#1d3570]/30 rounded-xl p-4 text-slate-300">
                <p>
                  If you continue to browse and use this website, you are
                  agreeing to comply with and be bound by the following terms
                  and conditions of use, which together with our privacy policy
                  govern Bytemine Technologies Ltd relationship with you in
                  relation to this website. If you disagree with any part of
                  these terms and conditions, please do not use our website.
                </p>
              </div>

              {/* Section 1 */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-white flex items-center gap-2 border-l-2 border-[--accent-primary] pl-3">
                  1. Terms of Use &amp; Site Content
                </h3>
                <div className="space-y-2 text-slate-400 pl-4">
                  <p>
                    The term &apos;Level2&apos; or &apos;us&apos; or
                    &apos;we&apos; refers to the owner of the website. The term
                    &apos;you&apos; refers to the user or viewer of our website.
                  </p>
                  <ul className="list-disc pl-5 space-y-1.5">
                    <li>
                      The content of the pages of this website is for your
                      general information and use only. It is subject to change
                      without notice.
                    </li>
                    <li>
                      This website uses cookies to monitor browsing preferences.
                    </li>
                  </ul>
                </div>
              </div>

              {/* Section 2 */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-white flex items-center gap-2 border-l-2 border-[--accent-primary] pl-3">
                  2. Warranties &amp; Liability Limitation
                </h3>
                <div className="space-y-2 text-slate-400 pl-4">
                  <p>
                    Neither we nor any third parties provide any warranty or
                    guarantee as to the accuracy, timeliness, performance,
                    completeness or suitability of the information and materials
                    found or offered on this website for any particular purpose.
                  </p>
                  <p>
                    You acknowledge that such information and materials may
                    contain inaccuracies or errors and we expressly exclude
                    liability for any such inaccuracies or errors to the fullest
                    extent permitted by law.
                  </p>
                  <p>
                    Your use of any information or materials on this website is
                    entirely at your own risk, for which we shall not be liable.
                    It shall be your own responsibility to ensure that any
                    products, services or information available through this
                    website meet your specific requirements.
                  </p>
                </div>
              </div>

              {/* Section 3 */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-white flex items-center gap-2 border-l-2 border-[--accent-primary] pl-3">
                  3. Intellectual Property &amp; Trademarks
                </h3>
                <div className="space-y-2 text-slate-400 pl-4">
                  <p>
                    This website contains material which is owned by or licensed
                    to us. This material includes, but is not limited to, the
                    design, layout, look, appearance and graphics. Reproduction
                    is prohibited other than in accordance with the copyright
                    notice, which forms part of these terms and conditions.
                  </p>
                  <p>
                    All trademarks reproduced in this website, which are not the
                    property of, or licensed to the operator, are acknowledged
                    on the website.
                  </p>
                  <p>
                    Unauthorised use of this website may give rise to a claim
                    for damages and/or be a criminal offence.
                  </p>
                </div>
              </div>

              {/* Section 4 */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-white flex items-center gap-2 border-l-2 border-[--accent-primary] pl-3">
                  4. External Links &amp; Governing Law
                </h3>
                <div className="space-y-2 text-slate-400 pl-4">
                  <p>
                    From time to time, this website may also include links to
                    other websites. These links are provided for your
                    convenience to provide further information. They do not
                    signify that we endorse the website(s). We have no
                    responsibility for the content of the linked website(s).
                  </p>
                  <p>
                    Your use of this website and any dispute arising out of such
                    use of the website is subject to the laws of England,
                    Northern Ireland, Scotland and Wales.
                  </p>
                </div>
              </div>

              {/* Section 5 */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-white flex items-center gap-2 border-l-2 border-[--accent-primary] pl-3">
                  5. Investment Risk &amp; Financial Disclaimers
                </h3>
                <div className="space-y-2 text-slate-400 pl-4">
                  <p>
                    The Level2 service and its content should not be construed
                    as a solicitation to invest and/or trade. You should seek
                    independent advice in this regard.
                  </p>
                  <p>
                    Past performance is not indicative of future performance. No
                    representation is being made that any results discussed
                    within the service and its related media content will be
                    achieved.
                  </p>
                  <p>
                    All opinions, news, research, analyses, prices or other
                    information is provided as general market commentary and not
                    as investment advice. Level2, their members, shareholders,
                    employees, agents, representatives and resellers do not
                    warrant the completeness, accuracy or timeliness of the
                    information supplied, and they shall not be liable for any
                    loss or damages, consequential or otherwise, which may arise
                    from the use or reliance of the Level2 service and its
                    content.
                  </p>
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="border-t border-border/50 pt-4 flex justify-end">
            <DialogClose asChild>
              <Button
                type="button"
                className="bg-[--accent-primary] text-white hover:bg-[--accent-primary]/90 px-6 py-2 rounded-md transition-all duration-300"
              >
                Accept &amp; Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
export default TermsAndConditions;
